import { useMutation } from "@tanstack/react-query";
import apiClient from "../client";
import { graphScopes } from "../../config/auth";
import { accessTokenService } from "../../services/access-token-service";

const GRAPH_CHANGE_PASSWORD_URL = "https://graph.microsoft.com/v1.0/me/changePassword";

export interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
}

export type PasswordChangeRejection = "wrongCurrentPassword" | "rejected";

/** Microsoft turned the change down, as opposed to a fault or no answer at all. */
export class PasswordChangeRejectedError extends Error {
  constructor(
    readonly reason: PasswordChangeRejection,
    message: string
  ) {
    super(message);
  }
}

// Graph answers every refusal with the same code, Request_BadRequest, so the message is the only thing
// that says which field was wrong. Matched on the parameter it names rather than its wording.
const WRONG_CURRENT_PASSWORD = /paramName:\s*oldPassword\b/i;

// Changes the signed-in customer's password with Microsoft, then asks our API to email them about it.
// The password goes only to Microsoft; our API never sees it.
export function useChangePassword() {
  return useMutation({
    mutationFn: async (input: ChangePasswordInput) => {
      const token = await accessTokenService.getAccessToken(graphScopes.changePassword);
      const response = await fetch(GRAPH_CHANGE_PASSWORD_URL, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(input),
      });

      if (response.status === 400) {
        const message = await readGraphError(response);
        throw new PasswordChangeRejectedError(
          WRONG_CURRENT_PASSWORD.test(message) ? "wrongCurrentPassword" : "rejected",
          message
        );
      }
      if (!response.ok) {
        throw new Error(`Graph changePassword returned ${response.status}: ${await readGraphError(response)}`);
      }

      // The password has changed whatever happens here, so a failed notice must not read as a failed change.
      try {
        await apiClient.post("/customers/me/password/changed");
      } catch (err) {
        console.warn("Password changed, but the notice email could not be requested", err);
      }
    },
  });
}

async function readGraphError(response: Response): Promise<string> {
  try {
    const body = await response.json();
    return body?.error?.message ?? response.statusText;
  } catch {
    return response.statusText;
  }
}
